import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { SwotNodes, SwotNode } from './SwotAnalysis';
import './SwotGrid.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface SwotGridSection {
  label: string;
  nodes: SwotNodes;
}

export interface SwotGridProps {
  /** Top section: typically "Status Quo" — heavy on weaknesses */
  statusQuo: SwotGridSection;
  /** Bottom section: typically "Recommended Option" — heavy on strengths */
  recommended: SwotGridSection;
  onNodeClick?: (nodeId: string, type: keyof SwotNodes) => void;
}

/* ------------------------------------------------------------------ */
/*  Quadrant config                                                    */
/* ------------------------------------------------------------------ */

const QUADRANT_CONFIG: Record<
  keyof SwotNodes,
  { title: string; colorClass: string }
> = {
  strengths: { title: 'What Works', colorClass: 'swot-grid-q--green' },
  weaknesses: { title: 'Holding it Back', colorClass: 'swot-grid-q--red' },
  opportunities: { title: 'Opportunities', colorClass: 'swot-grid-q--blue' },
  threats: { title: 'Threats', colorClass: 'swot-grid-q--amber' },
};

/* ------------------------------------------------------------------ */
/*  Memoised quadrant                                                  */
/* ------------------------------------------------------------------ */

interface QuadrantProps {
  type: keyof SwotNodes;
  nodes: SwotNode[];
  onNodeClick?: (nodeId: string, type: keyof SwotNodes) => void;
}

const Quadrant = React.memo<QuadrantProps>(function Quadrant({
  type,
  nodes,
  onNodeClick,
}) {
  const cfg = QUADRANT_CONFIG[type];
  // Dynamic flex-grow: more nodes = more visual weight
  const grow = Math.max(1, nodes.length);

  return (
    <div
      className={`swot-grid-q ${cfg.colorClass}`}
      style={{ flexGrow: grow }}
    >
      <h4 className="swot-grid-q__title">{cfg.title}</h4>
      <div className="swot-grid-q__nodes">
        <AnimatePresence>
          {nodes.map((node, i) => (
            <motion.button
              key={node.id}
              className="swot-grid-node"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ delay: i * 0.03 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onNodeClick?.(node.id, type)}
            >
              {node.icon && <span className="swot-grid-node__icon">{node.icon}</span>}
              <span className="swot-grid-node__label">{node.label}</span>
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/*  Section (a full 4-quadrant SWOT)                                   */
/* ------------------------------------------------------------------ */

interface SectionProps {
  section: SwotGridSection;
  variant: 'problem' | 'solution';
  onNodeClick?: (nodeId: string, type: keyof SwotNodes) => void;
}

const Section = React.memo<SectionProps>(function Section({
  section,
  variant,
  onNodeClick,
}) {
  return (
    <div className={`swot-grid-section swot-grid-section--${variant}`}>
      <h3 className="swot-grid-section__label">{section.label}</h3>
      <div className="swot-grid-section__quadrants">
        <div className="swot-grid-section__row">
          <Quadrant type="strengths" nodes={section.nodes.strengths} onNodeClick={onNodeClick} />
          <Quadrant type="weaknesses" nodes={section.nodes.weaknesses} onNodeClick={onNodeClick} />
        </div>
        <div className="swot-grid-section__row">
          <Quadrant type="opportunities" nodes={section.nodes.opportunities} onNodeClick={onNodeClick} />
          <Quadrant type="threats" nodes={section.nodes.threats} onNodeClick={onNodeClick} />
        </div>
      </div>
    </div>
  );
});

/* ------------------------------------------------------------------ */
/*  Main SwotGrid component (portrait vertical stack)                  */
/* ------------------------------------------------------------------ */

export const SwotGrid: React.FC<SwotGridProps> = ({
  statusQuo,
  recommended,
  onNodeClick,
}) => {
  return (
    <div className="swot-grid-stack">
      {/* Status Quo — the problem */}
      <Section section={statusQuo} variant="problem" onNodeClick={onNodeClick} />

      {/* Arrow separator */}
      <div className="swot-grid-arrow">
        <div className="swot-grid-arrow__icon">↓</div>
        <div className="swot-grid-arrow__text">Recommended Upgrade</div>
      </div>

      {/* Recommended Option — the solution */}
      <Section section={recommended} variant="solution" onNodeClick={onNodeClick} />
    </div>
  );
};
